import React, { useMemo } from 'react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import DashboardMetricItem from '@components/pages/Dashboard/DashboardMetricItem.component'
import type { RangeKey } from '@dataTypes/dashboard.types'
import type { NoShowRateOverviewResult } from '@services/api/dashboard/no-show-rate.api'
import { buildNoShowRateOverviewFromMock } from '@utils/noShowRateMock.util'
import { isDashboardDevMocksEnabled } from '@utils/dashboardDevMocks.util'
import {
  hasNonZeroDashboardValues,
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import { metricDataToItemPropsWithLayoutClass } from '@utils/dashboardMetrics.util'

type NoShowRateCardProps = {
  range: RangeKey
  noShowRateOverview: NoShowRateOverviewResult | null
  loading: boolean
}

export function NoShowRateCard({
  range,
  noShowRateOverview,
  loading,
}: NoShowRateCardProps): React.ReactElement {
  const useMocks = isDashboardDevMocksEnabled()
  const mockOverview = useMemo(
    () => buildNoShowRateOverviewFromMock(range),
    [range],
  )
  const lineChart = useMemo(() => {
    if (useMocks) return mockOverview.lineChart
    return (
      noShowRateOverview?.lineChart ?? {
        data: [],
        yDomain: [0, 3] as [number, number],
        yTicks: [0, 1, 2, 3],
      }
    )
  }, [useMocks, mockOverview.lineChart, noShowRateOverview])
  const metrics = useMemo(() => {
    if (useMocks) return mockOverview.metrics
    return (
      noShowRateOverview?.metrics ?? [
        { id: 'ns-lost-chair', leftLabel: 'Lost chair time', value: '0 hrs' },
        { id: 'ns-scheduled', leftLabel: 'Scheduled', value: '0%' },
        { id: 'ns-no-show', leftLabel: 'No-show', value: '0' },
      ]
    )
  }, [useMocks, mockOverview.metrics, noShowRateOverview])
  const isEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      noShowRateOverview?.noShowCount,
      noShowRateOverview?.scheduledPct,
      noShowRateOverview?.lostChairTime,
      ...lineChart.data.map((point) => point.value),
      ...metrics.map((metric) => metric.value),
    ),
  )
  const pending = isDashboardSectionPending(loading, noShowRateOverview != null)

  return (
    <DashboardSectionCard
      title="No-show rate"
      titleHref="/dashboard/no-show-rate"
      className="dashboard-overview-line-chart-card"
      loading={pending}
      skeleton={{ metrics: 3, block: 'chart' }}
      isEmpty={isEmpty}
      disableTitleLink={isEmpty}
    >
      <div className="dashboard-overview-line-chart-content">
        <div className="dashboard-overview-acceptance-metrics-row">
          {metrics.map((metric) => (
            <DashboardMetricItem
              key={metric.id ?? metric.leftLabel}
              {...metricDataToItemPropsWithLayoutClass(
                metric,
                'dashboard-overview-acceptance-metric',
              )}
            />
          ))}
        </div>
        <div className="dashboard-overview-line-chart-wrap">
          <ResponsiveContainer width="100%" height={280}>
            <LineChart
              data={lineChart.data}
              margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="0"
                vertical={false}
                horizontal
                stroke="var(--gray-200)"
              />
              <XAxis
                type="category"
                dataKey="label"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: 'var(--gray-400)' }}
                tickMargin={8}
              />
              <YAxis
                type="number"
                domain={lineChart.yDomain}
                ticks={lineChart.yTicks}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 14, fill: 'var(--gray-400)' }}
                tickFormatter={(value: number) =>
                  value === 0 ? '0' : `${value} hrs`
                }
                width={48}
              />
              <Tooltip
                formatter={(value: number) => [`${value} hrs`, '']}
                contentStyle={{ fontSize: 11, borderRadius: 6 }}
                labelFormatter={(label) => String(label)}
              />
              <Line
                type="linear"
                dataKey="value"
                stroke="var(--blue-500)"
                strokeWidth={2}
                dot={false}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </DashboardSectionCard>
  )
}
